import { FC, ReactNode, useEffect, useId, useRef, useState } from "react";

interface InfoTooltipProps {
  content: ReactNode;
  variant?: "dark" | "light";
  children: ReactNode;
}

const InfoTooltip: FC<InfoTooltipProps> = ({ content, children }) => {
  const tooltipId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setIsOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={rootRef} className="relative">
      <div
        role="button"
        aria-expanded={isOpen}
        aria-controls={tooltipId}
        onClick={() => setIsOpen((o) => !o)}
        className="cursor-pointer"
      >
        {children}
      </div>
      {isOpen && (
        <div
          id={tooltipId}
          role="tooltip"
          className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2"
        >
          <div className="text-dark w-[200px] rounded-md bg-[#d4d4d4] px-3 py-2 text-center text-[13px] leading-tight font-normal whitespace-normal shadow-xl">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoTooltip;
